/**
 * Sprint Redesign v1 (Do 03.09.2026) — Redesign-Layout-Shell.
 *
 * Layout-Wrapper für alle Redesign-Pages:
 *   ┌─────────────────────────────────┐
 *   │ TopBar (persistent, sticky)     │
 *   ├──────────┬──────────────────────┤
 *   │          │                      │
 *   │ Sidebar  │      Content         │
 *   │ (fixed)  │                      │
 *   │          │                      │
 *   └──────────┴──────────────────────┘
 *
 * Auth-Guard: 401 → /login. Lädt user via /api/user/dashboard und reicht
 * `data`/`reload` an Kinder weiter (analog zu DashboardLayout).
 *
 * Sprint-Backlog Redesign v1 · Items 1 + 2 + 3.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import RedesignSidebar from "@/components/redesign/RedesignSidebar";
import RedesignTopBar from "@/components/redesign/RedesignTopBar";
import VersionFooter from "@/components/redesign/VersionFooter";
import FeedbackPrompt from "@/components/redesign/FeedbackPrompt";
import MarketingBanner from "@/components/marketing/MarketingBanner";
import CampaignModal from "@/components/marketing/CampaignModal";
import { api } from "@/lib/api";
import { useViewAs } from "@/context/ViewAsContext";
import { trackPurchase as pixelTrackPurchase } from "@/lib/metaPixel";
import { trackPurchase as ga4TrackPurchase } from "@/lib/ga4";
// Sprint V98.1 (Do 17.09.2026 20:30) — Webinar-Banner global fuer Free-User
// auf ALLEN Redesign-Seiten (Mein-Muster, Andere-Verstehen, Wirkung-Gestalten,
// Videokurs, Anliegen, Profil). Direkt unter der TopBar, prominent.
import FreeWebinarBanner from "@/components/dashboard/FreeWebinarBanner";
import { journeyFixesOn } from "@/lib/journeyFlags";

export default function RedesignLayout({ children, requireAuth = true }) {
  const navigate = useNavigate();
  const { override } = useViewAs();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // I27: ESC schließt Mobile-Drawer
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const handleKey = (e) => { if (e.key === "Escape") setMobileNavOpen(false); };
    document.addEventListener("keydown", handleKey);
    // Body-Scroll-Lock während Drawer offen
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [mobileNavOpen]);

  const loadData = useCallback(async () => {
    if (!requireAuth) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/user/dashboard");
      setData(data);
      setError(null);
    } catch (e) {
      if (e?.response?.status === 401) {
        if (journeyFixesOn() && window.location.pathname.startsWith("/login")) return;
        const next = encodeURIComponent(window.location.pathname);
        navigate(`/login?next=${next}`, { replace: true });
        return;
      }
      setError("Konnte den Mitgliederbereich nicht laden.");
    } finally {
      setLoading(false);
    }
  }, [navigate, requireAuth]);

  useEffect(() => { loadData(); }, [loadData]);

  // Sprint V28 (Fr 27.02.2026) — Purchase-Track-Handoff:
  // Backend markiert im User-Doc `purchase_track_pending=true`, sobald der
  // Ablefy-Webhook einen echten (non-trial) Kauf gemeldet hat. Sobald das
  // Cockpit geladen ist, feuern wir Browser-Pixel `Purchase` + GA4
  // `purchase` mit denselben Event-IDs (Meta merged serverseitig+browserseitig
  // via event_id). ACK an Backend leert die Flags dauerhaft — bei Renewals
  // feuert nichts erneut, weil `first_paid_at` idempotent bleibt.
  useEffect(() => {
    const user = data?.user;
    if (!user || !user.purchase_track_pending) return;
    const value = Number(user.purchase_track_value || 0);
    if (!(value > 0)) return;
    try {
      pixelTrackPurchase({
        value,
        currency: user.purchase_track_currency || "EUR",
        plan: user.purchase_track_plan || "pro_solo_monthly",
        orderId: user.purchase_track_order_id || null,
        eventId: user.purchase_track_event_id || null,
      });
      ga4TrackPurchase({
        value,
        currency: user.purchase_track_currency || "EUR",
        plan: user.purchase_track_plan || "pro_solo_monthly",
        orderId: user.purchase_track_order_id || null,
      });
    } catch (_e) { /* fire-and-forget */ }
    // ACK an Backend — Flags leeren, damit beim naechsten Refresh nichts
    // erneut triggert. Fehler bleiben ohne UX-Effekt.
    api.post("/analytics/purchase-acked").catch(() => { /* silent */ });
  }, [data?.user?.purchase_track_pending, data?.user?.purchase_track_event_id]);

  const effectiveData = useMemo(() => {
    if (!data || !override) return data;
    return { ...data, user: { ...data.user, primary_fokustyp: override } };
  }, [data, override]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--fts-neutral-50)] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[var(--fts-primary-bright)] animate-spin" />
      </div>
    );
  }

  if (requireAuth && (error || !data)) {
    return (
      <div className="min-h-screen bg-[var(--fts-neutral-50)] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <p className="text-slate-600">{error || "Keine Daten."}</p>
          <button
            onClick={loadData}
            data-testid="redesign-layout-retry"
            className="mt-4 rounded-lg bg-[var(--fts-primary-bright)] text-white px-4 py-2 text-sm font-semibold"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[var(--fts-neutral-50)] dark:bg-slate-950 flex fts-redesign-root"
      data-testid="redesign-layout"
      data-redesign-root="true"
    >
      <RedesignSidebar mobileOpen={mobileNavOpen} onMobileClose={() => setMobileNavOpen(false)} />
      {/* Mobile-Backdrop */}
      {mobileNavOpen && (
        <div
          onClick={() => setMobileNavOpen(false)}
          data-testid="redesign-sidebar-backdrop"
          aria-hidden="true"
          className="lg:hidden fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm transition-opacity"
        />
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <RedesignTopBar onMobileMenuOpen={() => setMobileNavOpen(true)} />
        {/* Sprint V98.1 (Do 17.09.2026) — Webinar-Banner GLOBAL fuer Free-User
            direkt unter der TopBar, damit er auf ALLEN Redesign-Seiten
            prominent sichtbar ist. Rendert nichts fuer Pro-User (isFree-Check). */}
        <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-4">
          <FreeWebinarBanner user={effectiveData?.user} variant="prominent" />
        </div>
        {/* Sprint V102.j — Marketing-Banner-Slot (Trial-Push u.a.).
            Rendert intern nichts, wenn kein aktiver Banner fuer User-Tier. */}
        <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-3">
          <MarketingBanner />
        </div>
        <main className="flex-1" data-testid="redesign-main">
          {typeof children === "function"
            ? children({ data: effectiveData, reload: loadData })
            : children}
        </main>
        <div className="px-4 sm:px-6 lg:px-8 pb-6">
          <VersionFooter />
        </div>
      </div>
      {/* POSTLAUNCH-V6 · P2-#11 — Feedback-Prompt nach ~5 Min Session (dismissbar) */}
      <FeedbackPrompt />
      {/* Sprint V102.j — In-App-Campaign-Modal (Trial-Push u.a.). */}
      <CampaignModal />
    </div>
  );
}
